import {
    FC, useEffect,
    useRef,
} from 'react';

import { Button, ConfigProvider } from 'antd';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Can } from '@casl/react';
import { PaperClipOutlined } from '@ant-design/icons';
import {
    LANGUAGE_POLICY,
    RECIPIENT_EMAIL_FOR_LOGIN,
    OTP_ADMINS_RESTRICTED,
    OTP_COLLABORATORS_RESTRICTED,
} from '@/consts';
import { JUST_LOGGED_IN_TIMESTAMP_KEY, JUST_LOGGED_IN_TIMEOUT_MS } from '../Common/constants';
import Spinner from '../../Common/Spin';
import { PublicFooter } from '../../Common/PublicComponents';
import { BatchDownloadWidget } from '../../Common/OverflowWidget';
import {
    Header,
    useLanguageDefinition,
    useURLLinkId,
} from '../Common';
import { useStores, useTargetScreen, useLoginMethodPreference, LOGIN_METHOD_EMAIL } from '../../hooks';
import usePossibleRedirectToFolder from './usePossibleRedirectToFolder';
import appConfig from '../../../config/env';
import ability from '@/config/ability';
import { LANGUAGES } from '@/content/languages';
import styles from './RecipientPage.module.scss';
import { RecipientPageContent } from './InnerContent/RecipientPageContent';
import SharedOwnersLoaders from '@/components/Common/SendFilesBack/SharedOwnersLoaders';
import usePageLanguage from '../../hooks/usePageLanguage';
import { saveRedirectPath } from '@/components/utils';
import { trySignUp } from '@/components/hooks/useOTPFlow/helpers';
import { useNavigate, useLocation } from 'react-router-dom';

interface RecipientPageProps {
    isRootLoading: boolean;
}

const nameSpace = 'recipientPage.header';

const RecipientPage: FC<RecipientPageProps> = observer(({
    isRootLoading,
}) => {
    const { isLanguageDefined, defineLanguage } = useLanguageDefinition();
    const { i18n } = useTranslation();
    const { chooseLanguage } = usePageLanguage(i18n);
    const navigate = useNavigate();
    const location = useLocation();
    const { getLoginMethodPreference, setLoginMethodPreference, clearLoginMethodPreference } = useLoginMethodPreference();
    const hasRedirectedRef = useRef(false);

    const {
        recipientPageStore,
        userStore,
        authSettingsStore,
    } = useStores();

    const isMobile = useTargetScreen('md');

    const { fullScreen, collapsed, sharedFilesStore } = recipientPageStore;
    const {
        isLoading,
        isMainDataLoaded,
        linkData,
        anyRequestInProcess,
    } = sharedFilesStore;

    useURLLinkId((linkId) => sharedFilesStore.setLinkId(linkId));

    const isReadyToLoadData = !isRootLoading && !!sharedFilesStore.linkId;

    useEffect(() => {
        if (isMainDataLoaded && !isLanguageDefined) {
            const possibleLanguage = (
                sharedFilesStore.allFilesPoliciesAggregate[LANGUAGE_POLICY]?.value as LANGUAGES
                || appConfig.DEFAULT_LANGUAGE
            );
            if (!sharedFilesStore.errorType) {
                defineLanguage(possibleLanguage);
            }
            chooseLanguage(possibleLanguage);
        }
    }, [isMainDataLoaded, isLanguageDefined]);

    useEffect(() => {
        if (isReadyToLoadData) {
            sharedFilesStore.tryFetchLink();
        }
    }, [isReadyToLoadData]);

    const redirectToSignInWithProvisioning = async (): Promise<void> => {
        const currentPath = `${location.pathname}${location.search}`;
        saveRedirectPath(currentPath);

        const recipientEmail = sharedFilesStore.recipientEmail;
        if (recipientEmail) {
            sessionStorage.setItem(RECIPIENT_EMAIL_FOR_LOGIN, recipientEmail);
            try {
                await trySignUp(recipientEmail.toLowerCase(), authSettingsStore.API, {
                    linkId: sharedFilesStore.linkId,
                });
            } catch (error) {
                console.error('Failed to pre-provision Cognito user before login redirect:', error);
            }
        }

        navigate('/signIn');
    };

    useEffect(() => {
        if (isMainDataLoaded && !userStore.isUserSetUp && !hasRedirectedRef.current) {
            // Check if user just logged in to prevent redirect loop
            const justLoggedInTimestamp = sessionStorage.getItem(JUST_LOGGED_IN_TIMESTAMP_KEY);
            const hasJustLoggedIn = justLoggedInTimestamp
                && (Date.now() - parseInt(justLoggedInTimestamp, 10)) < JUST_LOGGED_IN_TIMEOUT_MS;

            if (hasJustLoggedIn) {
                return;
            }
            
            if (getLoginMethodPreference() === LOGIN_METHOD_EMAIL) {
                hasRedirectedRef.current = true;
                clearLoginMethodPreference();
                sessionStorage.removeItem(OTP_ADMINS_RESTRICTED);
                sessionStorage.removeItem(OTP_COLLABORATORS_RESTRICTED);
            }
        }
    }, [isMainDataLoaded, userStore.isUserSetUp, getLoginMethodPreference, clearLoginMethodPreference]);

    usePossibleRedirectToFolder(sharedFilesStore, userStore);

    const visibleFooter = (!isMobile && !fullScreen && !collapsed) || (!collapsed && fullScreen);

    const showLoginButton = !userStore.isUserSetUp;
    const handleLogin = () => {
        setLoginMethodPreference(LOGIN_METHOD_EMAIL);
        redirectToSignInWithProvisioning();
    };

    // Check if document is being viewed for mobile scrolling control
    const isDocumentViewing = isMobile && fullScreen && collapsed;

    return (
        <ConfigProvider direction={i18n.dir()}>
            <Spinner spinning={isLoading || anyRequestInProcess}>
                <div className={classNames(
                    styles.container,
                    { [styles['document-viewing']]: isDocumentViewing },
                    { [styles.fullScreen]: fullScreen }
                )}>
                    {!fullScreen && (
                        <Header>
                            <>
                                {showLoginButton && (
                                    <Button
                                        className={styles['send-back-button']}
                                        onClick={handleLogin}
                                    >
                                        {i18n.t('recipientPage.OTPSteps.login')}
                                    </Button>
                                )}
                                <Can I="SpecterxShareFiles" a="ALL" ability={ability}>
                                    <div className={styles['buttons-container']}>
                                        <SharedOwnersLoaders
                                            allowMultipleSharing
                                            sourceEmail={linkData?.src_email || ''}
                                            renderButton={(onClick) => (
                                                (
                                                    <div>
                                                        {appConfig.SHOW_SHARE_BACK_BUTTON && (
                                                            <Button
                                                                className={styles['send-back-button']}
                                                                onClick={onClick}
                                                                type="link"
                                                                shape="round"
                                                                icon={<PaperClipOutlined />}
                                                            >
                                                                {i18n.t(`${nameSpace}.buttons.shareAFileBack`)}
                                                            </Button>
                                                        )}
                                                    </div>
                                                )
                                            )}
                                        />
                                    </div>
                                </Can>
                            </>
                        </Header>
                    )}
                    <RecipientPageContent isLanguageDefined={isLanguageDefined} />
                    {visibleFooter && <PublicFooter showSkeleton={!isLanguageDefined} />}
                </div>
            </Spinner>
            <BatchDownloadWidget wrapperClassName={styles['recipient-popup']} />
        </ConfigProvider>
    );
});

export default RecipientPage;
